import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Checkbox } from '@liveauctioneers/hammer-ui-core/checkbox';
import { BodyPrimary, H1 } from '@liveauctioneers/hammer-ui-core/text';
import { TextInput } from '@liveauctioneers/hammer-ui-core/textInput';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { FormEventHandler, useCallback } from 'react';

import { usePrevious } from '@/hooks/usePrevious';
import useSetState from '@/hooks/useSetState';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    getCreateAccountError,
    getCreateAccountSubmitted,
} from '@/redux/modules/account/createAccount/createAccount.selectors';
import { AnalyticsTypes, EventContext } from '@/redux/modules/analytics';
import { getBidderExists } from '@/redux/modules/bidder/exists/bidderExists.selectors';
import { CompleteAccountModalContext, openCompleteAccountSignUpModal } from '@/redux/modules/modal';
import { GlobalState } from '@/redux/store';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { email as isValidEmail } from '@/utils/validation';

import { CreateUserRecaptcha } from '../CreateUserRecaptcha/CreateUserRecaptcha';
import { SubtleRouteLink } from '../RouteLinks/RouteLinks';

type Props = {
    shouldUseHomePageRedesign?: boolean;
};

export const JoinContainer = ({ shouldUseHomePageRedesign = false }: Props) => {
    const formatMessage = useAppFormatMessage();
    const dispatch = useAppDispatch();
    const [state, setState] = useSetState({
        email: '',
        emailValid: true,
        termsAccepted: false,
    });

    const prevState = usePrevious(state);

    const bidderExists = useAppSelector((state: GlobalState) => getBidderExists(state));
    const createAccountError = useAppSelector(getCreateAccountError);
    const createAccountSubmitted = useAppSelector(getCreateAccountSubmitted);

    const onEmailChange = useCallback<FormEventHandler<HTMLInputElement>>(
        (event) => {
            setState({ email: event.currentTarget.value });
        },
        [setState]
    );

    const openEmailVerificationModal = useCallback(
        ({ email }: { email: string }) => {
            dispatch(
                openCompleteAccountSignUpModal({
                    emailAddress: email,
                    eventContext: EventContext.homepageBanner,
                    eventName: AnalyticsTypes.Track.StartedAccountCreation,
                    id: 0,
                    type: CompleteAccountModalContext.EmailVerification,
                })
            );
        },
        [dispatch]
    );

    const onJoinWithEmail = useCallback(async () => {
        const email = state.email.trim();
        const emailValid = isValidEmail(email);
        setState({ emailValid });
        if (emailValid) {
            openEmailVerificationModal({ email });
            return;
        }
    }, [openEmailVerificationModal, setState, state.email]);

    const toggleTermsAccepted = useCallback(() => {
        setState({ termsAccepted: !state.termsAccepted });
    }, [setState, state.termsAccepted]);

    const errorOnPage = (!state.emailValid && state.email.length !== 0) || (createAccountError && !bidderExists);

    return (
        <div
            className={cn(
                'relative flex h-[220px] w-[490px] flex-col justify-around',
                // smaller screens
                'smMax:h-auto smMax:w-full smMax:items-center smMax:p-0 smMax:text-center',
                shouldUseHomePageRedesign ? 'h-auto w-[516px] justify-start self-start' : 'mr-lg'
            )}
        >
            <CreateUserRecaptcha />
            <H1 className={cn(!shouldUseHomePageRedesign && 'mt-6')}>{formatMessage(['lets_go_treasure_hunting'])}</H1>
            <BodyPrimary
                className={cn('w-full', shouldUseHomePageRedesign ? 'mb-md max-w-full' : 'mb-[14px] max-w-[428px]')}
            >
                {formatMessage(['discover_and_win'])}
            </BodyPrimary>
            <div
                className={cn(
                    'flex w-full flex-col smMax:mx-auto',
                    shouldUseHomePageRedesign ? 'max-w-full' : 'max-w-[487px]'
                )}
            >
                {' '}
                <div
                    className="mb-md flex"
                    suppressHydrationWarning
                >
                    <TextInput
                        aria-label={String(formatMessage(['signUpModal', 'ariaLabel', 'emailInput']))}
                        className="flex-grow smMax:w-full smMax:[&>span]:flex"
                        data-testid="emailInput"
                        hasError={errorOnPage}
                        id="join-container-email-input"
                        onChange={onEmailChange}
                        placeholder={String(formatMessage(['signUpModal', 'enterEmail']))}
                    />
                    <Button
                        className="h-[42px] w-[100px]"
                        data-testid="joinButton"
                        disabled={
                            createAccountSubmitted || prevState === undefined
                                ? null
                                : (state.email.length === 0 && prevState.email.length === 1) ||
                                  (state.termsAccepted === false && state.email.length !== 0)
                        }
                        loading={createAccountSubmitted}
                        onClick={onJoinWithEmail}
                    >
                        {formatMessage(['setPasswordModal', 'submit'])}
                    </Button>
                </div>
                {(state.email.length > 0 || state.termsAccepted === true) && (
                    <Checkbox.Option
                        checked={state.termsAccepted}
                        name="adBannerTermsAcceptedCheckbox"
                        onChange={toggleTermsAccepted}
                    >
                        {formatMessage(['signUpModal', 'acceptText'], {
                            values: {
                                cookieLink: (
                                    <SubtleRouteLink
                                        key="join-container-cookie-link"
                                        onClick={(e) => e.preventDefault()}
                                        rel="noopener"
                                        target="_blank"
                                        to="/cookie-policy"
                                    >
                                        {formatMessage(['signUpModal', 'cookieLink'])}
                                    </SubtleRouteLink>
                                ),
                                tosLink: (
                                    <SubtleRouteLink
                                        key="join-container-terms-link"
                                        onClick={(e) => e.preventDefault()}
                                        rel="noopener"
                                        target="_blank"
                                        to="/termsandconditions"
                                    >
                                        {formatMessage(['signUpModal', 'termsLink'])}
                                    </SubtleRouteLink>
                                ),
                            },
                        })}
                    </Checkbox.Option>
                )}
            </div>
        </div>
    );
};
